import { Component, EventEmitter, Input, Output } from '@angular/core';

import { TPipe } from '../../../pipe/t.pipe';

/**
 * Standardized loading / error / empty state block. Render either this
 * component **or** the actual data — exclusivity is the caller's job :
 *
 *   @if (error() || loading() || items().length === 0) {
 *     <app-data-state
 *       [loading]="loading()"
 *       [error]="error()"
 *       [empty]="items().length === 0"
 *       emptyMessage="Aucun utilisateur."
 *       (retry)="load()" />
 *   } @else {
 *     <table>…</table>
 *   }
 *
 * The three states are mutually exclusive in priority order :
 * error → loading → empty. If none is true the component renders nothing.
 */
@Component({
  selector: 'app-data-state',
  template: `
    @if (error) {
      <div class="px-5 py-12 text-center">
        <p class="text-sm text-error-700 dark:text-error-400">{{ error }}</p>
        @if (retry.observed) {
          <button
            type="button"
            (click)="retry.emit()"
            class="mt-3 text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
          >
            {{ retryLabel | t }}
          </button>
        }
      </div>
    } @else if (loading) {
      <div class="px-5 py-16 text-center">
        <svg class="mx-auto mb-3 h-6 w-6 animate-spin text-brand-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
          <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
        </svg>
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ loadingMessage | t }}</p>
      </div>
    } @else if (empty) {
      <div class="px-5 py-16 text-center">
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ emptyMessage | t }}</p>
      </div>
    }
  `,
  imports: [TPipe],
})
export class DataStateComponent {
  @Input() loading = false;
  /** Message d'erreur **dynamique** (back / `toErrorMessage`) — volontairement
   *  PAS passé par `| t` : ce n'est pas une clé, c'est déjà du texte final. */
  @Input() error: string | null = null;
  @Input() empty = false;
  // i18n : ces 3 entrées acceptent **une clé** (`common.loading`…) ou du texte
  // brut — `t()` retombe sur la clé, donc le FR passé en dur reste affiché tel
  // quel. Migration page par page possible sans casser l'existant.
  @Input() loadingMessage = 'common.loading';
  @Input() emptyMessage = 'common.empty';
  @Input() retryLabel = 'common.retry';

  /** Emitted when the user clicks the retry button. Wire it to refetch. */
  @Output() readonly retry = new EventEmitter<void>();
}
